<template>
    <Modal :alwaysShow="true" :header="headerText" @hide="$emit('hide')">
        <div v-if="loading">
            <span class="fa fa-cog fa-spin"></span>
            Loading...
        </div>

        <template v-if="!loading">
            <FormGroup title="Vendor" v-if="vendor">
                <p class="form-control-static">{{ vendor.name }}</p>
            </FormGroup>

            <FormGroup :form="form" title="Order Quantity" field="quantity">
                <input type="number" min="0" class="form-control" v-model="form.data.quantity" :disabled="saving" v-focus.select @keyup.enter="save" />
            </FormGroup>

            <div class="form-group">
                <label>Notes</label>
                <textarea class="form-control" v-model="form.data.notes" :disabled="saving" v-autosize></textarea>
            </div>

            <div v-if="otherUsersCurrentOrderItemsForThisVendor.length">
                <div><strong>Other current order items for this product</strong></div>

                <ul class="list-group">
                    <OtherOrderItemRow v-for="orderItem in otherUsersCurrentOrderItemsForThisVendor" :orderItem="orderItem" :key="orderItem.id" />
                </ul>
            </div>

            <div v-if="currentOrderItemsForOtherVendors.length">
                <div><strong>Current order items with other vendors</strong></div>

                <ul class="list-group">
                    <OtherOrderItemRow v-for="orderItem in currentOrderItemsForOtherVendors" :orderItem="orderItem" :key="orderItem.id" show-vendor />
                </ul>
            </div>
        </template>

        <template slot="footer" v-if="!loading">
            <button class="btn btn-danger" v-if="!isNew" @click="confirmDelete" :disabled="saving">
                <span class="fa fa-trash"></span>
                Remove Order Item
            </button>

            <button class="btn btn-primary" @click="save" :disabled="saving">
                <span class="fa fa-check"></span>
                Save Order Item
            </button>
        </template>
    </Modal>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import Form from '@/helpers/Form';

export default {
    components: {
        OtherOrderItemRow: require('./OtherOrderItemRow.vue').default,
    },
    props: ['orderItemId', 'inventoryItemId', 'vendorId', 'productId'],
    data() {
        return {
            loading: false,
            saving: false,
            discoveredOrderItemId: null,
            form: new Form({
                id: '',
                quantity: 1,
                notes: '',
            }),
        };
    },
    computed: {
        ...mapGetters('data', ['orderItemsForProductAndVendorIds']),
        ...mapState('data', {
            orderItem(state) {
                return state.orderItems.byId[this.effectiveOrderItemId];
            },
            vendor(state) {
                if (this.isNew)
                    return state.vendors.byId[this.vendorId];

                return this.orderItem
                    && state.orders.byId[this.orderItem.order_id]
                    && state.vendors.byId[state.orders.byId[this.orderItem.order_id].vendor_id];
            },
            currentOrderItemsForThisVendor() {
                return this.orderItemsForProductAndVendorIds({
                    productId: this.productId,
                    vendorId: this.vendorId,
                });
            },
            currentOrderItemsForOtherVendors() {
                return this.orderItemsForProductAndVendorIds({
                    productId: this.productId,
                    vendorId: this.vendorId,
                    showOtherVendorsInstead: true,
                });
            },
            myCurrentOrderItemForThisVendor(state) {
                return this.currentOrderItemsForThisVendor.find(i => i.user_id == Laravel.userId);
            },
            otherUsersCurrentOrderItemsForThisVendor(state) {
                return this.currentOrderItemsForThisVendor.filter(i => i.user_id != Laravel.userId);
            },
        }),

        effectiveOrderItemId() {
            if (this.myCurrentOrderItemForThisVendor)
                return this.myCurrentOrderItemForThisVendor.id;
            return this.orderItemId;
        },
        isNew() {
            return !this.effectiveOrderItemId;
        },
        headerText() {
            return (this.isNew) ? 'New Order Item' : 'Edit Order Item';
        },
    },
    created() {
        this.loadOrderItems();
    },
    methods: {
        ...mapActions('data', [
            'getOrderItem',
            'createOrderItem',
            'updateOrderItem',
            'deleteOrderItem',
            'getOrderItemsForInventoryItem',
        ]),
        async loadOrderItems() {
            this.loading = true;
            this.discoveredOrderItemId = null;

            if (this.inventoryItemId)
                await this.getOrderItemsForInventoryItem({ inventoryItemId: this.inventoryItemId, productId: this.productId });
            else if (this.orderItemId)
                await this.getOrderItem({ id: this.orderItemId });

            this.form.updateData(this.orderItem);

            this.loading = false;
        },
        async save() {
            this.saving = true;

            try {
                let saveMethod = this.isNew ? 'createOrderItem' : 'updateOrderItem';
                let orderItem = await this.form.save(() => this[saveMethod]({
                    orderItem: this.form.data,
                    productId: this.productId,
                    inventoryItemId: this.inventoryItemId,
                }));

                this.$emit('saved', orderItem);
            } catch (e) {
                console.log(e)
                if (!e.isValidationError) {
                    let message = _.get(e, 'response.data.message', 'Please try again later');
                    swal('Couldn\'t save order item', message, 'error');
                }
            }

            this.saving = false;
        },
        async confirmDelete() {
            try {
                await swal({
                    type: 'warning',
                    title: 'Delete order item?',
                    text: 'This order item will be removed from the order.',
                    showCancelButton: true,
                    showLoaderOnConfirm: true,
                    preConfirm: () => this.performDelete(),
                });
            } catch (e) {
                let message = get(e, 'response.data.message', 'Please try again later.');
                swal('Error', message, 'error');
            }
        },
        async performDelete() {
            await this.deleteOrderItem({ orderItem: this.orderItem });
            this.$emit('deleted');
        },
    },
};
</script>
